export async function httpRequest(
  params: FetchParams & {
    middlewares?: FetchMiddleware[];
  },
) {
  const request: FetchRequest = {
    url: params.url,
    method: params.method ?? "GET",
    headers: new Headers(params.headers),
    query: params.query || {},
    body: params.body ?? null,
    allowedStatuses: params.allowedStatuses || [],
    attempt: 1,
  };
  const middlewares = params.middlewares || [];
  for (const middleware of middlewares) {
    if (middleware.preFetch) await middleware.preFetch(request);
  }
  let response: Response;
  let retry: boolean;
  do {
    retry = false;

    const url = createUrl(request);
    const { body, contentType } = getBodyAndContentType(request);
    if (contentType && !request.headers.has("Content-Type")) {
      request.headers.set("Content-Type", contentType);
    }

    try {
      response = await fetch(url, {
        headers: request.headers,
        method: request.method,
        body,
      });
    } catch (error) {
      // Network-level failure (no connection, DNS, etc.)
      for (const middleware of middlewares) {
        const result = await middleware.onNetworkError?.(request, error);
        if (result && result.retry) {
          retry = true;
          request.attempt += 1;
          break;
        }
      }
      if (retry) continue;
      throw error;
    }
    for (const middleware of middlewares) {
      const result = await middleware.postFetch?.(request, response);
      if (result && result.retry) {
        retry = true;
        request.attempt += 1;
        break;
      }
    }
  } while (retry);
  return response;
}

export type JsonBody = Record<string, unknown> | unknown[];

export type FetchParams = {
  url: string;
  method?: FetchMethod;
  headers?: Record<string, string>;
  query?: Record<string, string>;
  body?: JsonBody | BodyInit | null;
  allowedStatuses?: number[];
};

export type FetchRequest = {
  url: string;
  method: FetchMethod;
  headers: Headers;
  query: Record<string, string>;
  body: JsonBody | BodyInit | null;
  allowedStatuses: number[];
  attempt: number;
};

export type FetchMethod = "GET" | "POST" | "PUT" | "PATCH" | "DELETE";

export type FetchMiddleware = {
  preFetch?(request: FetchRequest): void | Promise<void>;
  postFetch?(
    request: FetchRequest,
    response: Response,
  ): void | PostFetchResult | Promise<void> | Promise<PostFetchResult>;
  onNetworkError?(
    request: FetchRequest,
    error: unknown,
  ): void | PostFetchResult | Promise<void> | Promise<PostFetchResult>;
};

type PostFetchResult = { retry?: boolean };

export function fetchMiddlewareRetry(statusCodes: number[]): FetchMiddleware {
  const backoff = async (request: FetchRequest) => {
    const attempt = request.attempt ?? 1;
    if (attempt < 3) {
      await sleep(1000 * Math.pow(2, attempt));
      return { retry: true };
    }
  };
  return {
    onNetworkError: backoff,
    async postFetch(request, response) {
      if (statusCodes.includes(response.status)) return backoff(request);
    },
  };
}

function createUrl(requestData: FetchRequest) {
  const query = new URLSearchParams(
    Object.entries(requestData.query).map(([k, v]) => [k, String(v)]),
  );
  if (query.size === 0) return requestData.url;
  if (requestData.url.includes("?")) return `${requestData.url}&${query}`;
  return `${requestData.url}?${query}`;
}

function getBodyAndContentType(request: FetchRequest) {
  let body: BodyInit | null = null;
  let contentType: string | undefined;
  if (request.body) {
    if (
      typeof request.body === "object" &&
      (Object.getPrototypeOf(request.body) === Object.prototype ||
        Array.isArray(request.body))
    ) {
      body = JSON.stringify(request.body);
      contentType = "application/json";
    } else if (request.body instanceof Blob) {
      body = request.body;
      contentType = request.body.type || "application/octet-stream";
    } else if (typeof request.body === "string") {
      body = request.body;
      contentType = "text/plain";
    } else {
      body = request.body as BodyInit;
    }
  }
  return { body, contentType };
}

function sleep(duration: number) {
  return new Promise<void>((resolve) => setTimeout(resolve, duration));
}
